import { useAppSelector } from '@hooks/app/redux';
import { LogoutOutlined, PersonFilledOutlined } from '@icon';
import { TokenService } from '@services/internal';
import { Dropdown, MenuItemType, Typography } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { TCurrentUser } from '../../types/users';
import Avatar from '../avatar';
import { TopbarUserSC } from './topbar.styled';

const getUserName = ({ profile }: TCurrentUser): string => {
  const { firstName, lastName } = profile;
  return `${firstName} ${lastName}`;
};

interface TopbarUserProps {
  authorized?: boolean;
}

const TopbarUser: React.FC<TopbarUserProps> = ({ authorized }) => {
  const user = useAppSelector((store) => store.users.currentUser);
  const { push } = useHistory();
  const { formatMessage } = useIntl();

  const dropItems = React.useMemo(() => {
    const items: MenuItemType[] = [];

    if (!user) {
      return items;
    } else {
      items.push({
        key: 'handle',
        label: (
          <>
            {`${user.profile.firstName} ${user.profile.lastName}`}
            <br />
            {user.email}
          </>
        ),
        disabled: true,
      });
    }

    if (authorized) {
      items.push(
        ...([
          { type: 'divider' },
          {
            key: 'profile',
            Icon: PersonFilledOutlined,
            label: formatMessage({ id: 'user_drop.my_profile' }),
            onClick: () => {
              push('/my-profile');
            },
          },
        ] as MenuItemType[]),
      );
    }

    items.push(
      ...([
        { type: 'divider' },
        {
          key: 'logout',
          Icon: LogoutOutlined,
          label: formatMessage({ id: 'user_drop.logout' }),
          variant: 'danger',
          onClick: () => {
            TokenService.revokeSession();
          },
        },
      ] as MenuItemType[]),
    );

    return items;
  }, [formatMessage, user, authorized]);

  if (!user) {
    return null;
  }

  return (
    <TopbarUserSC>
      <Dropdown
        menu={{ items: dropItems as any[] }}
        placement={'topRight'}
        align={{ offset: [-8, -8] }}
        mouseEnterDelay={0.2}
      >
        <div className="tb-drop-wrapper">
          <Typography variant="strong" noWrap>
            {getUserName(user)}
          </Typography>
          <div className="tb-avatar">
            <Avatar isDoctor size={30} imgUrl={user?.profile.profileImage} />
          </div>
        </div>
      </Dropdown>
    </TopbarUserSC>
  );
};

export default React.memo(TopbarUser);
